import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Snackbar,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  PlayArrow as PlayIcon,
  Stop as StopIcon,
  People as PeopleIcon,
} from '@mui/icons-material';
import { api } from '../../../../services/authService';
import theme from '../../../../theme/theme';
import ConfirmDeleteDialog from '../../../../components/ConfirmDeleteDialog';

const LeadNurturingCampaigns = () => {
  const navigate = useNavigate();
  const [accounts, setAccounts] = useState([]);
  const [selectedAccount, setSelectedAccount] = useState('');
  const [crmCampaigns, setCrmCampaigns] = useState([]);
  const [selectedCrmCampaign, setSelectedCrmCampaign] = useState('');
  const [nurturingCampaigns, setNurturingCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
  });

  // Fetch accounts on component mount
  useEffect(() => {
    fetchAccounts();
  }, []);

  // Fetch CRM campaigns when account changes
  useEffect(() => {
    if (selectedAccount) {
      fetchCrmCampaigns(selectedAccount);
    } else {
      setCrmCampaigns([]);
      setSelectedCrmCampaign('');
    }
  }, [selectedAccount]);

  // Fetch nurturing campaigns when account or CRM campaign changes
  useEffect(() => {
    if (selectedAccount) {
      fetchNurturingCampaigns();
    } else {
      setNurturingCampaigns([]);
    }
  }, [selectedAccount, selectedCrmCampaign]);

  const fetchAccounts = async () => {
    try {
      const response = await api.get('/crm/accounts/');
      setAccounts(response.data);
    } catch (error) {
      console.error('Error fetching accounts:', error);
      setError('Failed to fetch accounts');
    }
  };

  const fetchCrmCampaigns = async (accountId) => {
    try {
      const response = await api.get(`/crm/accounts/${accountId}/campaigns/`);
      setCrmCampaigns(response.data);
    } catch (error) {
      console.error('Error fetching CRM campaigns:', error);
      setError('Failed to fetch CRM campaigns');
    }
  };

  const fetchNurturingCampaigns = async () => {
    setLoading(true);
    try {
      let url = '/acs/nurturing-campaigns/';
      const params = new URLSearchParams();
      
      if (selectedAccount) {
        params.append('account', selectedAccount);
      }
      if (selectedCrmCampaign) {
        params.append('crm_campaign', selectedCrmCampaign);
      }

      const response = await api.get(`${url}?${params.toString()}`);
      setNurturingCampaigns(response.data);
      setError(null);
    } catch (error) {
      console.error('Error fetching nurturing campaigns:', error);
      setError('Failed to fetch nurturing campaigns');
    } finally {
      setLoading(false);
    }
  };

  const handleAccountChange = (event) => {
    setSelectedAccount(event.target.value);
  };

  const handleCrmCampaignChange = (event) => {
    setSelectedCrmCampaign(event.target.value);
  };

  const getCampaignTypeLabel = (type) => {
    const types = {
      journey: 'Journey Based',
      drip: 'Drip Campaign',
      reminder: 'Reminder Campaign',
      blast: 'One-time Blast',
    };
    return types[type] || type;
  };

  const getChannelLabel = (channel) => {
    const channels = {
      email: 'Email',
      sms: 'SMS',
      voice: 'Voice',
      chat: 'Chat',
    };
    return channels[channel] || channel;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const handleToggleActive = async (campaign) => {
    try {
      if (campaign.active) {
        // Deactivate campaign
        const response = await api.post(`/acs/nurturing-campaigns/${campaign.id}/deactivate/`);
        
        // Update the campaign in the local state
        setNurturingCampaigns(prevCampaigns =>
          prevCampaigns.map(c =>
            c.id === campaign.id ? { 
              ...c, 
              active: false,
              status: 'paused'
            } : c
          )
        );

        // Show success message
        setSnackbar({
          open: true,
          message: `Campaign "${campaign.name}" has been paused`,
          severity: 'success'
        });
      } else {
        // Activate campaign
        const response = await api.post(`/acs/nurturing-campaigns/${campaign.id}/activate/`);
        
        // Update the campaign in the local state
        setNurturingCampaigns(prevCampaigns =>
          prevCampaigns.map(c =>
            c.id === campaign.id ? { 
              ...c, 
              active: true,
              status: 'active'
            } : c
          )
        );

        // Show success message
        setSnackbar({
          open: true,
          message: `Campaign "${campaign.name}" has been activated`,
          severity: 'success'
        });
      }
    } catch (error) {
      console.error('Error toggling campaign status:', error);
      setError(error.response?.data?.error || 'Failed to update campaign status');
    }
  };

  const handleDelete = (campaign) => {
    setCampaignToDelete(campaign);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!campaignToDelete) return;

    try {
      await api.delete(`/acs/nurturing-campaigns/${campaignToDelete.id}/`);
      
      // Remove the campaign from the local state
      setNurturingCampaigns(prevCampaigns =>
        prevCampaigns.filter(c => c.id !== campaignToDelete.id)
      );
      
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);

      // Show success message
      setSnackbar({
        open: true,
        message: `Campaign "${campaignToDelete.name}" has been deleted`,
        severity: 'success'
      });
    } catch (error) {
      console.error('Error deleting campaign:', error);
      setError('Failed to delete campaign');
    }
  };

  const handleCloseDeleteDialog = () => {
    setDeleteDialogOpen(false);
    setCampaignToDelete(null);
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  return (
    <Box sx={{ padding: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ color: theme.palette.text.primary }}>
          Lead Nurturing Campaigns
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddIcon />}
          onClick={() => navigate('/crm/lead-nurturing/create')}
        >
          Create New Campaign
        </Button>
      </Box>

      {/* Filters */}
      <Box sx={{ display: 'flex', gap: 2, mb: 3 }}>
        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Select Account</InputLabel>
          <Select
            value={selectedAccount}
            onChange={handleAccountChange}
            label="Select Account"
          >
            {accounts.map((account) => (
              <MenuItem key={account.id} value={account.id}>
                {account.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <FormControl sx={{ minWidth: 200 }}>
          <InputLabel>Select CRM Campaign</InputLabel>
          <Select
            value={selectedCrmCampaign}
            onChange={handleCrmCampaignChange}
            label="Select CRM Campaign"
            disabled={!selectedAccount}
          >
            <MenuItem value="">All Campaigns</MenuItem>
            {crmCampaigns.map((campaign) => (
              <MenuItem key={campaign.id} value={campaign.id}>
                {campaign.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      {/* Error Message */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* No Selection Message */}
      {!selectedAccount && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Please select an account to view lead nurturing campaigns.
        </Alert>
      )}

      {/* Campaigns Table */}
      {selectedAccount && (
        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Name</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Channel</TableCell>
                <TableCell>CRM Campaign</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Start Date</TableCell>
                <TableCell>End Date</TableCell>
                <TableCell>Participants</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {nurturingCampaigns.map((campaign) => (
                <TableRow key={campaign.id}>
                  <TableCell>{campaign.name}</TableCell>
                  <TableCell>{getCampaignTypeLabel(campaign.campaign_type)}</TableCell>
                  <TableCell>{getChannelLabel(campaign.channel)}</TableCell>
                  <TableCell>
                    {crmCampaigns.find(c => c.id === campaign.crm_campaign)?.name || 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={campaign.active ? 'Active' : 'Inactive'}
                      color={campaign.active ? 'success' : 'default'}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>{formatDate(campaign.start_date)}</TableCell>
                  <TableCell>{formatDate(campaign.end_date)}</TableCell>
                  <TableCell>{campaign.participants_count}</TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Tooltip title="Lead Selection">
                        <IconButton
                          size="small"
                          onClick={() => navigate(`/crm/lead-nurturing/${campaign.id}/lead-selection`)}
                        >
                          <PeopleIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Edit Campaign">
                        <IconButton
                          size="small"
                          onClick={() => {
                            const editPath = campaign.campaign_type === 'journey' 
                              ? `/crm/lead-nurturing/edit/journey/${campaign.id}`
                              : `/crm/lead-nurturing/edit/${campaign.campaign_type}/${campaign.id}`;
                            navigate(editPath);
                          }}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={campaign.active ? 'Deactivate' : 'Activate'}>
                        <IconButton
                          size="small"
                          onClick={() => handleToggleActive(campaign)}
                        >
                          {campaign.active ? <StopIcon /> : <PlayIcon />}
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete Campaign">
                        <IconButton
                          size="small"
                          onClick={() => handleDelete(campaign)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
              {nurturingCampaigns.length === 0 && (
                <TableRow>
                  <TableCell colSpan={8} align="center">
                    No campaigns found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Delete Confirmation Dialog */}
      <ConfirmDeleteDialog
        open={deleteDialogOpen}
        onClose={handleCloseDeleteDialog}
        onConfirm={confirmDelete}
        title="Delete Campaign"
        message="Are you sure you want to delete this campaign? This action cannot be undone."
      />

      {/* Success Notification */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default LeadNurturingCampaigns; 